import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as ActiveIcon,
  StarBorder as InactiveIcon,
  Edit as EditIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import setupService from '../../services/setup.service';
import SetupMindMap from '../../components/SetupMindMap';
import { useSetupStore } from '../../store/setupStore';
import { Setup } from '../../types';

const Setups: React.FC = () => {
  const [setups, setSetups] = useState<Setup[]>([]);
  const [selectedSetup, setSelectedSetup] = useState<Setup | null>(null);
  const [loading, setLoading] = useState(false);

  // Use the setup store for cache management
  const { setActiveSetup: setActiveSetupWithCache } = useSetupStore();

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSetupName, setNewSetupName] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingName, setEditingName] = useState('');

  // Load setups
  const loadSetups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await setupService.getAll();
      setSetups(data);
    } catch (error) {
      console.error('Error loading setups:', error);
      toast.error('Erreur lors du chargement des setups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSetups();
  }, [loadSetups]);

  // Create setup
  const handleCreate = async () => {
    if (!newSetupName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const newSetup = await setupService.create({ name: newSetupName.trim() });
      setSetups([...setups, newSetup]);
      setSelectedSetup(newSetup);
      setNewSetupName('');
      setCreateDialogOpen(false);
      toast.success('Setup créé avec succès');
    } catch (error) {
      console.error('Error creating setup:', error);
      toast.error('Erreur lors de la création du setup');
    }
  };

  // Delete setup
  const handleDelete = async (setup: Setup) => {
    if (setup.is_active) {
      toast.error('Impossible de supprimer le setup actif');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${setup.name}" ?`)) {
      return;
    }

    try {
      await setupService.delete(setup.id);
      setSetups(setups.filter(s => s.id !== setup.id));
      if (selectedSetup?.id === setup.id) {
        setSelectedSetup(null);
      }
      toast.success('Setup supprimé');
    } catch (error: any) {
      console.error('Error deleting setup:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Set active setup (uses store to clear cache)
  const handleSetActive = async (setup: Setup) => {
    try {
      // Use the store's setActiveSetup which clears cache and calls API
      await setActiveSetupWithCache(setup.id);
      setSetups(setups.map(s => ({
        ...s,
        is_active: s.id === setup.id
      })));
      toast.success(`"${setup.name}" est maintenant le setup actif`);
    } catch (error) {
      console.error('Error setting active setup:', error);
      toast.error('Erreur lors de l\'activation du setup');
    }
  };

  // Toggle public
  const handleTogglePublic = async (setup: Setup) => {
    try {
      const updated = await setupService.update(setup.id, { is_public: !setup.is_public });
      setSetups(setups.map(s => s.id === setup.id ? updated : s));
      if (selectedSetup?.id === setup.id) {
        setSelectedSetup(updated);
      }
    } catch (error) {
      console.error('Error toggling public:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Update setup name
  const handleUpdateName = async () => {
    if (!selectedSetup || !editingName.trim()) return;

    try {
      const updated = await setupService.update(selectedSetup.id, { name: editingName.trim() });
      setSetups(setups.map(s => s.id === updated.id ? updated : s));
      setSelectedSetup(updated);
      setEditDialogOpen(false);
      toast.success('Nom modifié');
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Select setup
  const handleSelectSetup = async (setup: Setup) => {
    try {
      // Reload full setup with nodes
      const fullSetup = await setupService.getSetup(setup.id);
      setSelectedSetup(fullSetup);
    } catch (error) {
      console.error('Error loading setup:', error);
      toast.error('Erreur lors du chargement du setup');
    }
  };

  // Refresh selected setup
  const handleSetupChange = async () => {
    if (selectedSetup) {
      const updated = await setupService.getSetup(selectedSetup.id);
      setSelectedSetup(updated);
      setSetups(setups.map(s => s.id === updated.id ? updated : s));
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Setups List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Mes Setups</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                size="small"
              >
                Nouveau
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : setups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Aucun setup. Créez-en un pour commencer.
              </Typography>
            ) : (
              <List>
                {setups.map((setup) => (
                  <ListItem
                    key={setup.id}
                    button
                    selected={selectedSetup?.id === setup.id}
                    onClick={() => handleSelectSetup(setup)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      border: '1px solid',
                      borderColor: selectedSetup?.id === setup.id ? 'primary.main' : 'divider',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetActive(setup);
                      }}
                      sx={{ mr: 1 }}
                      title={setup.is_active ? 'Setup actif' : 'Définir comme actif'}
                    >
                      {setup.is_active ? (
                        <ActiveIcon color="primary" />
                      ) : (
                        <InactiveIcon />
                      )}
                    </IconButton>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TreeIcon fontSize="small" color="action" />
                          <span>{setup.name}</span>
                          {setup.is_public && (
                            <Chip label="Public" size="small" color="success" variant="outlined" />
                          )}
                          {setup.is_modified && setup.original_author && (
                            <Chip label="Modifié" size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        setup.original_author_name
                          ? `Original par ${setup.original_author_name}`
                          : undefined
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(setup);
                        }}
                        disabled={setup.is_active}
                        title={setup.is_active ? 'Impossible de supprimer le setup actif' : 'Supprimer'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Setup Editor */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, minHeight: 500 }}>
            {selectedSetup ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">{selectedSetup.name}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingName(selectedSetup.name);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedSetup.is_public}
                          onChange={() => handleTogglePublic(selectedSetup)}
                        />
                      }
                      label="Public"
                    />
                    {!selectedSetup.is_active && (
                      <Button
                        variant="outlined"
                        startIcon={<ActiveIcon />}
                        onClick={() => handleSetActive(selectedSetup)}
                        size="small"
                      >
                        Activer
                      </Button>
                    )}
                  </Box>
                </Box>

                {selectedSetup.tags && selectedSetup.tags.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                    {selectedSetup.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}

                <SetupMindMap
                  setup={selectedSetup}
                  onSetupChange={handleSetupChange}
                />
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400,
                }}
              >
                <TreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Sélectionnez un setup
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choisissez un setup dans la liste pour le modifier
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau Setup</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du setup"
            fullWidth
            value={newSetupName}
            onChange={(e) => setNewSetupName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleCreate} variant="contained">
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Name Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le nom</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du setup"
            fullWidth
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleUpdateName();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleUpdateName} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Setups;
